import { RegionShared } from "@1games-ru/region-shared";
import { useState } from "react";
import * as rpc from "@1games-ru/rage-rpc";
import { Button, Card, Select, TextField } from "@radix-ui/themes";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response.ts";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response.ts";
import RpcMpProvider from "@/core/rpc-mp-provider/rpc-mp-provider.ts";
import { CreateHouseRequest } from "@/domains/dev/houseCreator/messages/CreateHouseRequest.ts";
import { CreateHouseResponse } from "@/domains/dev/houseCreator/messages/CreateHouseResponse.ts";
import { PlayerGetStateClientRequest } from "@/domains/dev/houseCreator/messages/PlayerGetStateClientRequest.ts";
import { PlayerGetStateClientResponse } from "@/domains/dev/houseCreator/messages/PlayerGetStateClientResponse.ts";

enum HouseLevel {
  Economy = "Economy",
  Standard = "Standard",
  Business = "Business",
  Luxury = "Luxury",
}

const interiors = [1, 2, 3, 4, 5, 6];

const garages = [1, 2, 3, 4];

type SavePoint = {
  position: { x: number; y: number; z: number };
  heading: number;
  dimension: number;
};

function HouseCreatorContainer() {
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [level, setLevel] = useState<HouseLevel>(HouseLevel.Economy);
  const [interior, setInterior] = useState<number>(interiors[0]);
  const [garage, setGarage] = useState<number>(interiors[0]);
  const [price, setPrice] = useState<number>(100000);
  const [hasVehicle, setHasVehicle] = useState<boolean>(false);
  const [entrancePoint, setEntrancePoint] = useState<SavePoint | null>(null);
  const [garagePoint, setGaragePoint] = useState<SavePoint | null>(null);
  const [streetName, setStreetName] = useState<string | null>(null);

  const handleSelectLevelChange = (value: HouseLevel) => {
    setLevel(value);
  };

  const handleSelectInteriorChange = (value: string) => {
    setInterior(Number(value));
  };

  const handleSelectGarageChange = (value: string) => {
    setGarage(Number(value));
  };

  const getVehicleGarage = async () => {
    await RpcMpProvider.getInstance().callServer("HouseCreatorGarageVehicle", {
      method: hasVehicle ? "stop" : "start",
    });

    setHasVehicle(!hasVehicle);
  };

  const getPlayerState = (): Promise<
    RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<PlayerGetStateClientResponse>
  > => {
    return RpcMpProvider.getInstance().callClient<
      PlayerGetStateClientRequest,
      PlayerGetStateClientResponse
    >("PlayerGetStateAnyToClient", {});
  };

  const setupEntrancePoint = async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    const response = await getPlayerState();
    setLoading(false);

    if (response instanceof RpcMpProviderFailedResponse) {
      return;
    }

    const { position, dimension, heading } = response.data;

    setEntrancePoint({
      position: {
        x: position.x,
        y: position.y,
        z: position.z,
      },
      heading,
      dimension,
    });
  };

  const setupGaragePoint = async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    const response = await getPlayerState();
    setLoading(false);

    if (response instanceof RpcMpProviderFailedResponse) {
      return;
    }

    const { position, dimension, heading } = response.data;

    setGaragePoint({
      position: {
        x: position.x,
        y: position.y,
        z: position.z,
      },
      heading,
      dimension,
    });
  };

  const sendGetAddress = (position: typeof RegionShared.Types.World.Position) => {
    return RpcMpProvider.getInstance().callClient<
      typeof RegionShared.Client.Requests.GetAddressPositionStreetNameRequest,
      typeof RegionShared.Client.Responses.GetAddressPositionStreetNameResponse
    >(RegionShared.Client.Events.Address.GetAddressPositionStreetName, {
      position: {
        x: position.x,
        y: position.y,
        z: position.z,
      },
    });
  };

  const sendCreate = (
    targetStreetName: string,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.HouseCreateResponse>
  > => {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.HouseCreateRequest,
      typeof RegionShared.Backend.Responses.HouseCreateResponse
    >(RegionShared.Backend.Events.Estate.HouseCreate, {
      level,
      interiorId: interior,
      garageId: garage,
      entrance: {
        position: {
          x: entrancePoint.position.x,
          y: entrancePoint.position.y,
          z: entrancePoint.position.z,
        },
        heading: entrancePoint.heading,
      },
      garage: {
        position: {
          x: garagePoint.position.x,
          y: garagePoint.position.y,
          z: garagePoint.position.z,
        },
        heading: garagePoint.heading,
      },
      price,
      streetName: targetStreetName,
      dimension: 0,
    });
  };

  const create = async () => {
    if (!price) {
      setMessage("NO_PRICE");
      return;
    }

    if (!interior || !garage) {
      setMessage("NO_INTERIOR_OR_GARAGE");
      return;
    }

    if (!garagePoint || !entrancePoint) {
      setMessage("NO_GARAGE_POINT_OR_ENTRANCE_POINT");
      return;
    }

    if (loading) {
      return;
    }

    setLoading(true);

    const address = await sendGetAddress(entrancePoint.position);

    if (address instanceof RpcMpProviderSuccessResponse) {
      setStreetName(address.data.streetName);
    }

    if (address instanceof RpcMpProviderFailedResponse) {
      console.log({ address });
      setStreetName("Unknown Address");
      setLoading(false);
      return;
    }

    const response = await sendCreate(address.data.streetName);

    if (response instanceof RpcMpProviderSuccessResponse) {
      setGaragePoint(null);
      setEntrancePoint(null);
      setMessage("OK!");
    }

    if (response instanceof RpcMpProviderFailedResponse) {
      console.log({ response });
      setMessage(`Failed RPC: ${response.message}`);
    }

    setLoading(false);
  };

  return (
    <>
      <Card
        className="fixed bottom-10 right-10 p-5 w-[20rem] z-[999]"
        variant="solid-surface-gradient"
      >
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-full mb-4">
            <div className="mb-1">House Level</div>
            <Select.Root size="3" value={level} required onValueChange={handleSelectLevelChange}>
              <Select.Trigger />
              <Select.Content className="z-[999]">
                {Array.from(Object.values(HouseLevel)).map((houseLevel: HouseLevel) => (
                  <Select.Item key={houseLevel} value={houseLevel}>
                    {houseLevel}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>
          <div className="col-span-full mb-4">
            <div className="mb-1">Interior</div>
            <TextField.Root
              size="3"
              name="interior"
              onChange={(e) => handleSelectInteriorChange(e.target.value)}
              value={String(interior)}
              required
              placeholder="Введите id"
            />
          </div>
          <div className="col-span-full mb-4">
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-8">
                {entrancePoint === null ? (
                  "n/a"
                ) : (
                  <div>
                    <div>
                      {entrancePoint.position.x.toFixed(2)}, {entrancePoint.position.y.toFixed(2)},{" "}
                      {entrancePoint.position.z.toFixed(2)}
                    </div>
                    <div>
                      {entrancePoint.heading.toFixed(2)} + {entrancePoint.dimension}
                    </div>
                  </div>
                )}
              </div>
              <div className="col-span-4">
                <Button
                  size="3"
                  type="button"
                  className="w-full"
                  onClick={() => setupEntrancePoint()}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>

          <div className="col-span-full mb-4">
            <div className="mb-1">Garage</div>

            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-6">
                <TextField.Root
                  size="3"
                  name="garage"
                  onChange={(e) => handleSelectGarageChange(e.target.value)}
                  value={String(garage)}
                  required
                  placeholder="Введите id"
                />
              </div>

              <div className="col-span-6">
                <Button
                  size="3"
                  type="button"
                  className="w-full"
                  onClick={() => getVehicleGarage()}
                >
                  {hasVehicle ? "Вернуть" : "Дайте тачку!"}
                </Button>
              </div>
            </div>
          </div>
          <div className="col-span-full mb-4">
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-8">
                {garagePoint === null ? (
                  "n/a"
                ) : (
                  <div>
                    <div>
                      {garagePoint.position.x.toFixed(2)}, {garagePoint.position.y.toFixed(2)},{" "}
                      {garagePoint.position.z.toFixed(2)}
                    </div>
                    <div>
                      {garagePoint.heading.toFixed(2)} + {garagePoint.dimension}
                    </div>
                  </div>
                )}
              </div>
              <div className="col-span-4">
                <Button
                  size="3"
                  type="button"
                  className="w-full"
                  onClick={() => setupGaragePoint()}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
          <div className="col-span-full mb-4">
            <div className="mb-1">Price | Street: {streetName || "n/a"}</div>
            <TextField.Root
              size="3"
              name="price"
              onChange={(e) => setPrice(Number(e.target.value))}
              value={price}
              required
              placeholder="Введите цену"
            />
          </div>
          <div className="col-span-full mb-4">
            <Button size="3" type="button" className="w-full" onClick={() => create()}>
              Создать хату
            </Button>
          </div>

          <div className="col-span-full text-center">
            <div className="font-bold">Message:</div>
            <div>{message || "n/a"}</div>
          </div>
        </div>
      </Card>
    </>
  );
}

export default HouseCreatorContainer;
